import ExpandMore from "@mui/icons-material/ExpandMore";
import { Button, Divider, Menu, MenuList } from "@mui/material";
import { Dispatch, SetStateAction, createContext, useContext, useState } from "react";

import { Row } from "../ChemicalFacilitiesDataGrid";
import {
  AddFulfillmentNoteMenuItem,
  AddFulfillmentNoteDialog_ as AddFulfillmentNoteDialog,
} from "./AddFulfillmentNote";
import { CloseReportsDialog, CloseReportsMenuItem } from "./CloseReports";
import {
  OpenNewlyNotReportingDialog,
  OpenNewlyNotReportingMenuItem,
} from "./OpenNewlyNotReporting";
import { OpenRevisionMenuItem, OpenRevisionDialogs } from "./OpenRevision";
import { ResetReportsMenuItem, ResetReportsModal } from "./ResetReports";
import { SetDefaultExecutionPlanAuto } from "./SetDefaultExecutionPlanAuto";
import { SetDefaultExecutionPlanManual } from "./SetDefaultExecutionPlanManual";
import { SetNeedsRevisionMenuItem, SetNeedsRevisionDialog } from "./SetNeedsRevision";
import { VerifyAndSubmitDialog, VerifyAndSubmitMenuItem } from "./VerifyAndSubmit";
import { VerifyCredentialsMenuItem } from "./VerifyCredentials";

const buttonId = "bulk-actions-button";
const menuId = "bulk-actions-menu";

type BulkActionMenuProps = {
  selectedRows: Row[];
  reportingYear: number;
};

type DialogState =
  | "verify&submit"
  | "resetReports"
  | "closeReports"
  | "addFulfillmentNote"
  | "openRevisions"
  | "openNewlyNotReporting"
  | "setNeedsRevision"
  | null;

const bulkActionContext = createContext<{
  selectedRows: Row[];
  reportingYear: number;
  dialogState: DialogState;
  setDialogState: Dispatch<SetStateAction<DialogState>>;
  onClose: () => void;
}>({
  selectedRows: [],
  reportingYear: 0,
  dialogState: null,
  setDialogState: () => {},
  onClose: () => {},
});

export function useBulkActionContext() {
  return useContext(bulkActionContext);
}

const ChemicalFacilitiesActionsMenu = ({ selectedRows, reportingYear }: BulkActionMenuProps) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [dialogState, setDialogState] = useState<DialogState>(null);
  const open = Boolean(anchorEl);
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const onClose = () => {
    setAnchorEl(null);
    setDialogState(null);
  };

  return (
    <bulkActionContext.Provider
      value={{
        selectedRows,
        reportingYear,
        onClose,
        dialogState,
        setDialogState,
      }}
    >
      <Button
        id={buttonId}
        size="medium"
        aria-controls={open ? menuId : undefined}
        aria-haspopup="true"
        aria-expanded={open ? "true" : undefined}
        disabled={!selectedRows.length}
        variant="contained"
        color="staff"
        onClick={handleClick}
        endIcon={<ExpandMore />}
      >
        Bulk Actions
      </Button>
      <Menu
        id={menuId}
        anchorEl={anchorEl}
        open={open}
        onClose={onClose}
        MenuListProps={{ "aria-labelledby": buttonId }}
      >
        <MenuList>
          <OpenRevisionMenuItem />
          <OpenNewlyNotReportingMenuItem />
          <ResetReportsMenuItem />
          <CloseReportsMenuItem />
          <VerifyAndSubmitMenuItem />
          <VerifyCredentialsMenuItem />
          <Divider />
          <SetDefaultExecutionPlanManual />
          <SetDefaultExecutionPlanAuto />
          <Divider />
          <AddFulfillmentNoteMenuItem />
          <SetNeedsRevisionMenuItem />
        </MenuList>
      </Menu>
      <VerifyAndSubmitDialog />
      <ResetReportsModal />
      <CloseReportsDialog />
      <AddFulfillmentNoteDialog />
      <OpenRevisionDialogs />
      <OpenNewlyNotReportingDialog />
      <SetNeedsRevisionDialog />
    </bulkActionContext.Provider>
  );
};

export { ChemicalFacilitiesActionsMenu };
